import React, { useState } from 'react';
import { ConversationListItem } from '../../types';
import './ChatSidebar.css';

interface ChatSidebarProps {
  conversations: ConversationListItem[];
  currentConversationId?: string;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
  onDeleteConversations: (ids: string[]) => Promise<void>;
  onDeleteAllConversations: () => Promise<void>;
  isLoading?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  conversations,
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversations,
  onDeleteAllConversations,
  isLoading = false,
  isOpen = false,
  onClose,
}) => {
  const [isManageMode, setIsManageMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleSelectConversation = (id: string) => {
    if (isManageMode) {
      toggleSelection(id);
    } else {
      onSelectConversation(id);
      onClose?.();
    }
  };

  const handleNewConversation = () => {
    onNewConversation();
    onClose?.();
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === conversations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(conversations.map(c => c.conversationId));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    
    setIsDeleting(true);
    try {
      await onDeleteConversations(selectedIds);
      setSelectedIds([]);
      setIsManageMode(false);
    } catch (err) {
      console.error('Failed to delete selected:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      console.log('Starting delete all conversations...');
      await onDeleteAllConversations();
      console.log('Delete all completed successfully');
      setSelectedIds([]);
      setIsManageMode(false);
      setShowDeleteAllConfirm(false);
    } catch (err: any) {
      console.error('Failed to delete all:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete conversations';
      setError(errorMessage);
      // Don't close the dialog on error so user can see something went wrong
    } finally {
      setIsDeleting(false);
    }
  };

  const exitManageMode = () => {
    setIsManageMode(false);
    setSelectedIds([]);
    setShowDeleteAllConfirm(false);
  };

  return (
    <>
      {/* Mobile overlay */}
      <div 
        className={`sidebar-overlay ${isOpen ? 'visible' : ''}`}
        onClick={onClose}
      />
      
      <aside className={`chat-sidebar ${isOpen ? 'open' : ''}`}>
        {/* Mobile close button */}
        <button className="sidebar-close-btn" onClick={onClose} aria-label="Close sidebar">
          ✕
        </button>

        <button
          className="new-chat-btn"
          onClick={handleNewConversation}
          disabled={isLoading}
        >
          <span className="new-chat-icon">+</span>
          <span>New Chat</span>
        </button>

        <div className="conversations-list">
          {isLoading ? (
            <div className="conversations-loading">
              <div className="loading-spinner-small"></div>
              <span>Loading chats...</span>
            </div>
          ) : conversations.length === 0 ? (
            <p className="no-conversations">No conversations yet</p>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.conversationId}
                className={`conversation-item ${
                  conv.conversationId === currentConversationId ? 'active' : ''
                } ${isManageMode ? 'manage-mode' : ''}`}
                onClick={() => handleSelectConversation(conv.conversationId)}
              >
                {isManageMode && (
                  <input
                    type="checkbox"
                    className="conversation-checkbox"
                    checked={selectedIds.includes(conv.conversationId)}
                    onChange={() => toggleSelection(conv.conversationId)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <span className="conversation-icon">💬</span>
                <div className="conversation-info">
                  <span className="conversation-title">
                    Chat {conv.conversationId.slice(0, 8)}...
                  </span>
                  <span className="conversation-date">
                    {formatDate(conv.updatedAt)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {conversations.length > 0 && (
          <div className="sidebar-footer">
            {!isManageMode ? (
              <button
                className="clear-history-btn"
                onClick={() => setIsManageMode(true)}
                disabled={isLoading || isDeleting}
              >
                <span className="clear-icon">🗑️</span>
                <span>Manage Chats</span>
              </button>
            ) : (
              <div className="manage-mode-actions">
                {!showDeleteAllConfirm ? (
                  <>
                    <div className="manage-header">
                      <button
                        className="select-all-btn"
                        onClick={toggleSelectAll}
                        disabled={isDeleting}
                      >
                        {selectedIds.length === conversations.length ? '☑️ Deselect All' : '☐ Select All'}
                      </button>
                      <span className="selected-count">
                        {selectedIds.length} selected
                      </span>
                    </div>
                    
                    <div className="manage-buttons">
                      <button
                        className="delete-selected-btn"
                        onClick={handleDeleteSelected}
                        disabled={selectedIds.length === 0 || isDeleting}
                      >
                        {isDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.length})`}
                      </button>
                      
                      <button
                        className="delete-all-btn"
                        onClick={() => setShowDeleteAllConfirm(true)}
                        disabled={isDeleting}
                      >
                        Delete All ({conversations.length})
                      </button>
                    </div>
                    
                    <button
                      className="cancel-manage-btn"
                      onClick={exitManageMode}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <div className="confirm-dialog">
                    <p>⚠️ Delete ALL {conversations.length} conversation(s)?</p>
                    <p className="confirm-warning">This cannot be undone!</p>
                    {error && (
                      <p className="confirm-error">❌ {error}</p>
                    )}
                    <div className="confirm-actions">
                      <button
                        className="confirm-cancel"
                        onClick={() => {
                          setShowDeleteAllConfirm(false);
                          setError(null);
                        }}
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <button
                        className="confirm-delete"
                        onClick={handleDeleteAll}
                        disabled={isDeleting}
                      >
                        {isDeleting ? 'Deleting...' : 'Yes, Delete All'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
};
